import { loadChunkSize } from '@internal/blocks/SearchResultsBlock/loadChunkSize'
import { maxSearchResults } from '@internal/blocks/SearchResultsBlock/maxSearchResults'
import { SearchResultsSortKey } from '@internal/blocks/SearchResultsBlock/SearchResultsSortKey'
import { TRangeFiltersMap } from '@internal/blocks/SearchResultsBlock/TRangeFiltersMap'
import {
  mappedModelSuffix,
  SearchModel,
} from '@internal/blocks/SearchResultsBlock/TSearchParams'
import { SystemPageKey } from '@internal/page/SystemPageKey'
import { TLocale } from '@internal/utils/machine/i18n/TLocale'
import {
  MachineSubType,
  MachineType,
} from '@internal/utils/machine/MachineType'
import { TFilterableBaleChamberOption } from '@internal/utils/search/TFilterableBalerChamberOption'
import { TFilterableHeaderTypeOption } from '@internal/utils/search/TFilterableHeaderTypeOption'
import { MachineAttribute } from '../machine/MachineAttribute'
import { TSearchResultsPageRouteParams } from './routingTable'
import { urlFor } from './urls/urlFor'

export type SearchResultsParams = Omit<
  TSearchResultsPageRouteParams['queryParams'],
  | 'machineSubType'
  | 'baleChamber'
  | 'headerType'
  | 'amount'
  | 'brand'
  | 'location'
  | 'feature'
  | 'model'
> & {
  amount?: number
  baleChamber?: TFilterableBaleChamberOption
  headerType?: TFilterableHeaderTypeOption
  rangeFilters?: TRangeFiltersMap
  brands?: string[]
  locations?: string[]
  features?: MachineAttribute[]
  sort?: SearchResultsSortKey
  machineType?: MachineType
  machineSubType?: MachineSubType
  models?: SearchModel[]
}

const rangeFilterToQuery = (
  attribute: MachineAttribute,
  data: {
    min?: number
    max?: number
  }
) => {
  let value =
    data.min || data.max
      ? `${data.min ? Math.floor(data.min) : ''}-${
          data.max ? Math.floor(data.max) : ''
        }`
      : null

  if (!value) {
    return null
  }

  return {
    key: machineAttributeToSlug(attribute),
    value,
  }
}

const machineAttributeToSlug = (attribute: MachineAttribute) =>
  attribute.toLowerCase().replace(/_/g, '-')

export const getSearchResultsUrl = (
  locale: TLocale,
  params: Omit<SearchResultsParams, 'machineType'> & {
    machineTypeName?: string
  }
) => {
  const queryParams: TSearchResultsPageRouteParams['queryParams'] = {
    brand: params.brands,
    amount:
      params.amount && params.amount !== loadChunkSize
        ? Math.min(maxSearchResults, params.amount).toString()
        : undefined,
    location: params.locations?.map((location) => location.toLowerCase()),
    feature: params.features?.map(machineAttributeToSlug),
    sort:
      params.sort !== SearchResultsSortKey.RelevanceAsc
        ? params.sort
        : undefined,
  }

  queryParams.baseModel = []
  queryParams.model = []
  queryParams.modelSeries = []

  for (const model of params.models ?? []) {
    switch (model.type) {
      case 'base':
        queryParams.baseModel.push(model.name)
        break
      case 'mapped':
        queryParams.model.push(`${model.name}${mappedModelSuffix}`)
        break
      case 'fuzzy':
        queryParams.model.push(model.name)
        break
      case 'series':
        queryParams.modelSeries.push(model.name)
        break
    }
  }

  if (params.machineSubType) {
    queryParams.machineSubType = params.machineSubType.toString().toLowerCase()
  }

  if (params.baleChamber) {
    queryParams.baleChamber = params.baleChamber.toString()
  }

  if (params.headerType) {
    queryParams.headerType = params.headerType.toString()
  }

  if (params.rangeFilters) {
    Object.keys(params.rangeFilters).forEach((attribute) => {
      const data = params.rangeFilters![attribute]
      const query = rangeFilterToQuery(attribute as MachineAttribute, data)
      if (query) {
        ;(queryParams as any)[query.key] = query.value
      }
    })
  }
  const url = urlFor(locale, {
    key: SystemPageKey.SearchResults,
    machineTypeName: params.machineTypeName,
    queryParams,
  })

  return url
}
